import React from 'react';
import { Selection } from 'src/domains/sportsbook/webview/components/Selection';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { observer } from 'src/utils/mobx-react';
import { EventId, MarketId, SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import {
    HeaderCell,
    LineCell,
    MarketRows,
    SelectionCell,
    MatchWinnerRoot,
    HeaderWrapper,
} from './MatchWinnerMarketGroup.style';
import { SelectionsGroupWrap } from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified.style';

interface PropsType {
    eventId: EventId;
    markets: Array<MarketId>;
    selections: Record<number | string, SelectionModel[]>;
    isOpen: boolean;
    onClick(): void;
}

const INITIAL_ROWS_LIMIT = 4;

interface MatchWinnerRowType {
    key: string;
    line: number | null;
    home: { overSelectionId: SelectionId | undefined; underSelectionId: SelectionId | undefined };
    draw: { overSelectionId: SelectionId | undefined; underSelectionId: SelectionId | undefined };
    away: { overSelectionId: SelectionId | undefined; underSelectionId: SelectionId | undefined };
}

const OVER_SELECTION_NAMES = ['over'];
const UNDER_SELECTION_NAMES = ['under'];
const OVER_SELECTION_TEMPLATE_IDS = ['a-o', 'h-o', 'd-o', 'o', 'ov', 'over'];
const UNDER_SELECTION_TEMPLATE_IDS = ['a-u', 'h-u', 'd-u', 'u', 'un', 'under'];

const getSelectionWeight = (selection: SelectionModel, templateIds: Array<string>, names: Array<string>): number => {
    const templateId = selection.templateId?.toLowerCase().trim() ?? '';
    const name = selection.name.toLowerCase().trim();

    if (templateIds.includes(templateId)) {
        return 3;
    }
    if (name === names[0]) {
        return 2;
    }
    if (names.some((item) => name.includes(item))) {
        return 1;
    }
    return 0;
};

type MarketKey = 'h' | 'd' | 'a';
const getBestMatchingSelections = (
    marketSelections: Array<SelectionModel>,
    templateIds: Array<string>,
    names: Array<string>
): Map<MarketKey, SelectionModel> => {
    const matchingSelections = new Map<MarketKey, SelectionModel>();
    const addMatchingSelection = (selection: SelectionModel) => {
        const rawKey = selection.templateId?.split('-')[0]?.toLowerCase();

        const isMarketKey = (k: string): k is MarketKey => k === 'h' || k === 'd' || k === 'a';

        if (rawKey && isMarketKey(rawKey)) {
            matchingSelections.set(rawKey, selection);
        }
    };

    marketSelections.reduce<SelectionModel | undefined>((best, selection) => {
        const selectionWeight = getSelectionWeight(selection, templateIds, names);
        const bestWeight = best ? getSelectionWeight(best, templateIds, names) : 0;

        if (selectionWeight === 0) {
            return best;
        }

        if (best === undefined) {
            addMatchingSelection(selection);

            return selection;
        }

        if (selectionWeight > bestWeight) {
            return selection;
        }

        if (selectionWeight === bestWeight) {
            addMatchingSelection(selection);
        }

        return best;
    }, undefined);

    return matchingSelections;
};

const getMarketRows = (
    markets: Array<MarketId>,
    selections: Record<number | string, SelectionModel[]>
): MatchWinnerRowType[] => {
    return markets
        .map((marketId): MatchWinnerRowType | null => {
            const marketModel = marketId.getModel();
            if (marketModel === null) {
                return null;
            }

            const marketSelections = selections[marketModel.id] ?? [];

            const overSelections = getBestMatchingSelections(
                marketSelections,
                OVER_SELECTION_TEMPLATE_IDS,
                OVER_SELECTION_NAMES
            );
            const underSelections = getBestMatchingSelections(
                marketSelections,
                UNDER_SELECTION_TEMPLATE_IDS,
                UNDER_SELECTION_NAMES
            );

            let rawLine = (marketModel.selections[0]?.name ?? '').match(/\d+.\d/)?.[0];

            const line = rawLine ? parseFloat(rawLine) : null;

            return {
                key: `${marketModel.id}-${line ?? 'line-null'}`,
                line,
                home: {
                    overSelectionId: overSelections.get('h')?.id2,
                    underSelectionId: underSelections.get('h')?.id2,
                },
                draw: {
                    overSelectionId: overSelections.get('d')?.id2,
                    underSelectionId: underSelections.get('d')?.id2,
                },
                away: {
                    overSelectionId: overSelections.get('a')?.id2,
                    underSelectionId: underSelections.get('a')?.id2,
                },
            };
        })
        .filter((row): row is MatchWinnerRowType => row !== null)
        .sort((a, b) => {
            if (a.line === null && b.line === null) {
                return 0;
            }
            if (a.line === null) {
                return 1;
            }
            if (b.line === null) {
                return -1;
            }
            return a.line - b.line;
        });
};

const lineLabel = (line: number | null): string => {
    if (line === null) {
        return '-';
    }
    return Number.isInteger(line) ? `${line}.0` : `${line}`;
};

export const MatchWinnerMarketGroup = observer('MatchWinnerMarketGroup', (props: PropsType) => {
    const { markets, selections, isOpen, onClick } = props;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const rows = getMarketRows(markets, selections);
    const firstMarketModel = markets[0]?.getModel() ?? null;
    const visibleRows = isExpanded === true ? rows : rows.slice(0, INITIAL_ROWS_LIMIT);

    const event = props.eventId.getEventModel();

    if (event === null) {
        return null;
    }

    const market = event.markets[0] ?? null;
    const marketId = market === null ? undefined : market.id2;

    if (marketId === undefined) {
        return null;
    }

    const toggleExpand = (e: React.MouseEvent): void => {
        e.preventDefault();
        setIsExpanded((prev) => !prev);
    };

    if (firstMarketModel === null) {
        return null;
    }

    return (
        <MatchWinnerRoot className='market-group--total-goals'>
            <MarketHeader
                name={firstMarketModel.template.name || 'Total Goals'}
                isOpen={isOpen}
                onClick={onClick}
                marketId={firstMarketModel.id2}
            />
            {isOpen ? (
                <>
                    <HeaderWrapper>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.goals'
                                defaultText='Goals'
                            />
                        </HeaderCell>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.home over'
                                defaultText='Home Over'
                            />
                        </HeaderCell>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.home under'
                                defaultText='Home Under'
                            />
                        </HeaderCell>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.draw over'
                                defaultText='Draw Over'
                            />
                        </HeaderCell>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.draw under'
                                defaultText='Draw Under'
                            />
                        </HeaderCell>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.away over'
                                defaultText='Away Over'
                            />
                        </HeaderCell>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.away under'
                                defaultText='Away Under'
                            />
                        </HeaderCell>
                    </HeaderWrapper>
                    <SelectionsGroupWrap>
                        <MarketRows>
                            {visibleRows.map((row) => {
                                return (
                                    <React.Fragment key={row.key}>
                                        <LineCell>{lineLabel(row.line)}</LineCell>
                                        <SelectionCell>
                                            <Selection selectionId={row.home.overSelectionId} />
                                        </SelectionCell>
                                        <SelectionCell>
                                            <Selection selectionId={row.home.underSelectionId} />
                                        </SelectionCell>
                                        <SelectionCell>
                                            <Selection selectionId={row.draw.overSelectionId} />
                                        </SelectionCell>
                                        <SelectionCell>
                                            <Selection selectionId={row.draw.underSelectionId} />
                                        </SelectionCell>
                                        <SelectionCell>
                                            <Selection selectionId={row.away.overSelectionId} />
                                        </SelectionCell>
                                        <SelectionCell>
                                            <Selection selectionId={row.away.underSelectionId} />
                                        </SelectionCell>
                                    </React.Fragment>
                                );
                            })}
                        </MarketRows>
                        {rows.length > INITIAL_ROWS_LIMIT ? (
                            <ExpandButton
                                isExpanded={isExpanded}
                                toggleExpand={toggleExpand}
                            />
                        ) : null}
                    </SelectionsGroupWrap>
                </>
            ) : null}
        </MatchWinnerRoot>
    );
});
